import { beDayOfWeekToWeekDay } from 'types/adapters/shared/beDayOfWeekToWeekDay'
import { asCseId, asMasterStopId, asRouteId, asRouteSlotId } from 'types/entities/shared/ids'

import { deriveRecurrence } from './deriveRecurrence'

import type { MasterStopApi } from 'types/api/route/masterStop'
import type { Address, GeoPoint } from 'types/entities/Address'
import type { MasterStop, RoutableMasterStop } from 'types/modules/routing'

const ADAPTER_NAME = 'fromMasterStopApi'

const buildAddress = (dto: MasterStopApi): Address | null => {
  if (!dto.line1) return null
  return {
    line1: dto.line1,
    unitNumber: dto.unitNumber,
    city: dto.city,
    state: dto.state,
    zipCode: dto.zipCode,
    country: dto.country,
  }
}

const buildGeo = (lat: number | null, lng: number | null): GeoPoint | null =>
  lat !== null && lng !== null ? { latitude: lat, longitude: lng } : null

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0

const warnBadField = (field: string, value: unknown, dtoId: unknown): void => {
  // eslint-disable-next-line no-console
  console.warn(`[${ADAPTER_NAME}] dropping row — bad ${field}`, { value, dtoId })
}

/**
 * A row the BE keys on `routeSlotId` rather than a configured service event — a disposal stop today.
 * Mirrors `isOnRequest` in `dailyStop.ts`: a null CSE is the shape of the row, not a malformed one.
 */
const isRoutable = (dto: MasterStopApi): boolean => dto.stopType != null && dto.stopType !== 'WORK_ORDER'

/**
 * Adapt a raw {@link MasterStopApi} row to a {@link MasterStop}, or a {@link RoutableMasterStop}
 * when it has no configured service event. A malformed required field warns and returns `null`.
 */
export const fromMasterStopApi = (dto: MasterStopApi): MasterStop | RoutableMasterStop | null => {
  if (!isNonEmptyString(dto.id)) {
    warnBadField('id', dto.id, dto.id)
    return null
  }

  const routable = isRoutable(dto)

  // A work-order row needs a CSE id; a routable row correctly carries null.
  if (!routable && !isNonEmptyString(dto.configuredServiceEventId)) {
    warnBadField('configuredServiceEventId', dto.configuredServiceEventId, dto.id)
    return null
  }
  if (!isNonEmptyString(dto.routeId)) {
    warnBadField('routeId', dto.routeId, dto.id)
    return null
  }
  if (typeof dto.sequence !== 'number' || !Number.isFinite(dto.sequence)) {
    warnBadField('sequence', dto.sequence, dto.id)
    return null
  }

  const recurrence = deriveRecurrence({
    daysOfWeek: dto.daysOfWeek,
    recurrenceFrequency: dto.recurrenceFrequency,
    recurrenceInterval: dto.recurrenceInterval,
    recurrencePer: dto.recurrencePer,
    pricedServiceType: dto.pricedServiceType,
    // Master rows are recurrence-level and cannot be one-offs — a one-off CSE has no
    // master-sequence row. `route_master_stops` exposes no event type to pass here.
    configuredServiceEventType: null,
  })

  const base = {
    id: asMasterStopId(dto.id),
    routeSlotId: dto.routeSlotId != null ? asRouteSlotId(dto.routeSlotId) : null,
    configuredServiceId: dto.configuredServiceId,
    endDate: dto.endDate,
    serviceEnded: dto.serviceEnded,
    serviceDeactivated: dto.serviceDeactivated,
    accountId: dto.accountId,
    accountName: dto.accountName,
    addressName: dto.addressName,
    locationName: dto.locationName,
    address: buildAddress(dto),
    geo: buildGeo(dto.latitude, dto.longitude),
    nearestRoad: buildGeo(dto.nearestRoadLatitude, dto.nearestRoadLongitude),
    serviceName: dto.serviceName,
    serviceActionType: dto.serviceActionType,
    eventActionType: dto.eventActionType,
    pricedServiceType: dto.pricedServiceType,
    recurrence,
    tags: dto.tags,
    routeAssignment: {
      routeId: asRouteId(dto.routeId),
      sequence: dto.sequence,
      dayOfWeek: beDayOfWeekToWeekDay(dto.dayOfWeek),
      serviceDate: null,
    },
  }

  if (routable) {
    return { ...base, kind: 'master-routable', configuredServiceEventId: null }
  }

  return { ...base, kind: 'master', configuredServiceEventId: asCseId(dto.configuredServiceEventId) }
}

/** List adapter — drops malformed rows (each emits its own `console.warn`). */
export const fromMasterStopListApi = (dtos: ReadonlyArray<MasterStopApi>): ReadonlyArray<MasterStop | RoutableMasterStop> =>
  dtos.map(fromMasterStopApi).filter((s): s is MasterStop | RoutableMasterStop => s !== null)
